import { useContext, useEffect, useState } from "react";
import { AuthContext } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  User, Shield, Link2, Users, RefreshCw, CheckCircle2, Info,
  Code, ExternalLink, Copy, Globe, Calendar, Trash2, Send,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import FormsManager from "@/components/FormsManager";

export default function SettingsPage() {
  const { user, setUser, API } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("");
  const [mondayBoardId, setMondayBoardId] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [removingUser, setRemovingUser] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const [removing, setRemoving] = useState(false);
  const [tab, setTab] = useState("profile");
  const isAdmin = user?.role === "admin";

  useEffect(() => { setBookingUrl(user?.booking_url || ""); setMondayBoardId(user?.monday_board_id || ""); }, [user]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await fetch(`${API}/profile`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ booking_url: bookingUrl, monday_board_id: mondayBoardId }),
      });
      if (res.ok) {
        const updated = await res.json();
        setUser((p) => ({ ...p, booking_url: updated.booking_url, monday_board_id: updated.monday_board_id }));
        toast.success("Profile saved");
      } else { toast.error("Failed to save"); }
    } catch { toast.error("Failed to save"); }
    finally { setSavingProfile(false); }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await fetch(`${API}/users`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch {
      console.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const updateRole = async (userId, role) => {
    try {
      await fetch(`${API}/users/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_id: userId, role }),
      });
      toast.success("Role updated");
      fetchUsers();
    } catch {
      toast.error("Failed to update role");
    }
  };

  const saveTerritory = async (userId, value) => {
    const territory = value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
    try {
      await fetch(`${API}/users/territory`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_id: userId, territory }),
      });
      toast.success("Territory updated");
      fetchUsers();
    } catch {
      toast.error("Failed to update territory");
    }
  };

  const removeUser = async () => {
    if (!removingUser) return;
    setRemoving(true);
    try {
      const qs = reassignTo ? `?reassign_to=${encodeURIComponent(reassignTo)}` : "";
      const res = await fetch(`${API}/users/${removingUser.user_id}${qs}`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const dest = reassignTo
          ? (users.find((u) => u.user_id === reassignTo)?.name || "the selected rep")
          : "Unassigned";
        toast.success(`User removed${data.reassigned_leads ? ` · ${data.reassigned_leads} lead(s) → ${dest}` : ""}`);
        setRemovingUser(null); setReassignTo(""); fetchUsers();
      } else {
        toast.error((await res.json()).detail || "Failed to remove user");
      }
    } catch { toast.error("Failed to remove user"); }
    finally { setRemoving(false); }
  };

  return (
    <div data-testid="settings-page" className="space-y-6 animate-fade-in-up">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your profile, integrations, and team
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList>
          <TabsTrigger data-testid="tab-profile" value="profile">
            <User className="w-3.5 h-3.5 mr-1" /> Profile
          </TabsTrigger>
          <TabsTrigger data-testid="tab-integrations" value="integrations">
            <Link2 className="w-3.5 h-3.5 mr-1" /> Integrations
          </TabsTrigger>
          <TabsTrigger data-testid="tab-team" value="team" onClick={fetchUsers}>
            <Users className="w-3.5 h-3.5 mr-1" /> Team
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger data-testid="tab-forms" value="forms">
              <Code className="w-3.5 h-3.5 mr-1" /> Forms
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile">
          <Card className="border-slate-200 shadow-sm max-w-2xl">
            <CardHeader>
              <CardTitle className="text-base">Your Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="text-lg bg-indigo-100 text-indigo-700">
                    {user?.name?.split(" ").map((n) => n[0]).join("") || "U"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold">{user?.name}</h3>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  <Badge variant="outline" className="mt-1 capitalize text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                    {user?.role || "admin"}
                  </Badge>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Full Name</Label>
                  <Input data-testid="profile-name" value={user?.name || ""} disabled className="mt-1 bg-slate-50" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Email</Label>
                  <Input data-testid="profile-email" value={user?.email || ""} disabled className="mt-1 bg-slate-50" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Role</Label>
                  <Input value={user?.role || "admin"} disabled className="mt-1 bg-slate-50 capitalize" />
                </div>
              </div>

              <Separator />

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Your Booking Link
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5 mb-1.5">
                  Your personal Calendly / Cal.com / Google Calendar link. Leads routed to you (round-robin, geography, or specific-rep forms) will be sent here to book.
                </p>
                <div className="flex gap-2">
                  <Input
                    data-testid="profile-booking-url"
                    value={bookingUrl}
                    onChange={(e) => setBookingUrl(e.target.value)}
                    placeholder="https://calendly.com/your-name"
                    className="flex-1"
                  />
                  <Button data-testid="save-booking-url" onClick={saveProfile} disabled={savingProfile}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0">
                    {savingProfile ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Save
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Send className="w-3 h-3" /> Your Monday.com Board ID
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5 mb-1.5">
                  Leads you sync to Monday are routed to <span className="font-medium">your</span> board. Find the ID at the end of your board URL: <code className="bg-slate-100 px-1 rounded">…/boards/<span className="font-semibold">1234567890</span></code>. Add columns titled e.g. Domain, Website, Phone, City, State, Category, Website Score, Presence Score, Priority Score, Status — we map by column title.
                </p>
                <div className="flex gap-2">
                  <Input
                    data-testid="profile-monday-board-id"
                    value={mondayBoardId}
                    onChange={(e) => setMondayBoardId(e.target.value)}
                    placeholder="e.g. 1234567890"
                    className="flex-1"
                  />
                  <Button data-testid="save-monday-board" onClick={saveProfile} disabled={savingProfile}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0">
                    {savingProfile ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Save
                  </Button>
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <Info className="w-4 h-4 text-muted-foreground mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  Profile information is managed through your Google account.
                  Contact an admin to change your role.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="integrations">
          <div className="space-y-4 max-w-2xl">
            {/* Monday CRM */}
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold">Monday Sales CRM</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Sync prospects and audit data to Monday.com
                      </p>
                    </div>
                  </div>
                  <Badge data-testid="monday-status-badge" variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
                  </Badge>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50/60 border border-emerald-100">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-emerald-800">
                      Live sync is enabled. The workspace API key is configured securely on the server. To route your leads, set <span className="font-medium">Your Monday.com Board ID</span> in the Profile tab.
                    </p>
                  </div>
                  <Button data-testid="monday-set-board-btn" size="sm" variant="outline"
                    onClick={() => setTab("profile")}>
                    Set your Board ID
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* GoHighLevel */}
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                      <Link2 className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold">GoHighLevel</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Sync contacts and tag with audit reports
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600 border-slate-200">
                    Coming Soon
                  </Badge>
                </div>
                <div className="mt-4 space-y-3">
                  <div>
                    <Label className="text-xs">API Key</Label>
                    <Input
                      data-testid="ghl-api-key"
                      placeholder="Enter GoHighLevel API key"
                      className="mt-1" type="password"
                    />
                  </div>
                  <Button data-testid="save-ghl" size="sm" variant="outline" disabled>
                    Connect (Coming Soon)
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <Info className="w-4 h-4 text-muted-foreground mt-0.5" />
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-emerald-700">Monday Sales CRM is live</span> — syncs run against the real Monday.com API using the server-configured workspace token, routed to each rep's own board. GoHighLevel support is coming soon.
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="team">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Team Members</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loadingUsers ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">User</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Role</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Territory (states)</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Joined</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.user_id} className="prospect-row">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="w-7 h-7">
                              <AvatarImage src={u.picture} />
                              <AvatarFallback className="text-xs bg-indigo-100 text-indigo-700">
                                {u.name?.split(" ").map((n) => n[0]).join("") || "U"}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-medium">{u.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                        <TableCell>
                          <Select
                            value={u.role}
                            onValueChange={(role) => updateRole(u.user_id, role)}
                            disabled={u.user_id === user?.user_id}
                          >
                            <SelectTrigger data-testid={`role-select-${u.user_id}`} className="w-32 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="sales_rep">Sales Rep</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            data-testid={`territory-input-${u.user_id}`}
                            defaultValue={(u.territory || []).join(", ")}
                            placeholder="TX, OK, LA"
                            className="h-8 w-40 text-xs uppercase"
                            onBlur={(e) => {
                              const next = e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean).join(", ");
                              const cur = (u.territory || []).join(", ");
                              if (next !== cur) saveTerritory(u.user_id, e.target.value);
                            }}
                            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          />
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            data-testid={`remove-user-${u.user_id}`}
                            size="sm"
                            variant="outline"
                            className="h-8 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50"
                            disabled={u.user_id === user?.user_id}
                            onClick={() => { setRemovingUser(u); setReassignTo(""); }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No team members found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Forms Tab (admin only) */}
        {isAdmin && (
          <TabsContent value="forms">
            <FormsManager />
          </TabsContent>
        )}
      </Tabs>

      {/* Remove user dialog */}
      <Dialog open={!!removingUser} onOpenChange={(o) => { if (!o) { setRemovingUser(null); setReassignTo(""); } }}>
        <DialogContent data-testid="remove-user-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove {removingUser?.name || removingUser?.email}?</DialogTitle>
            <DialogDescription>
              They'll immediately lose access. Choose what happens to any open leads currently owned by them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Their open leads should go to</Label>
            <Select value={reassignTo || "unassigned"} onValueChange={(v) => setReassignTo(v === "unassigned" ? "" : v)}>
              <SelectTrigger data-testid="reassign-leads-select" className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Leave Unassigned (a manager will reassign)</SelectItem>
                {users.filter((u) => u.user_id !== removingUser?.user_id).map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRemovingUser(null); setReassignTo(""); }}>Cancel</Button>
            <Button data-testid="confirm-remove-user" onClick={removeUser} disabled={removing}
              className="bg-rose-600 hover:bg-rose-700 text-white">
              {removing ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Remove User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
